import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { logsApi, syncApi, flowApi } from '../services/api';

const STALE_RUNNING_MS = 30 * 60 * 1000;

export function useSyncStatus(serviceName) {
  const { refresh } = useAuth();
  const [status, setStatus] = useState('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [executionId, setExecutionId] = useState(null);
  const [progress, setProgress] = useState('0 of 0');
  const [startAtMs, setStartAtMs] = useState(null);
  const [enabled, setEnabled] = useState(true);

  // Check latest summary log for current sync status
  const checkSummaryLogs = useCallback(async () => {
    try {
      const data = await logsApi.getSummaryLogs(serviceName);
      const summaryLog = data.summaryLog;
      if (summaryLog) {
        const fetchedStatus = (summaryLog.status || '').toLowerCase();
        const fetchedUpdatedAt = summaryLog.updatedAt || summaryLog.createdAt || summaryLog.formattedTimestamp || null;
        const updatedAtMs = fetchedUpdatedAt ? new Date(fetchedUpdatedAt).getTime() : null;
        const isStaleRunning = (
          (fetchedStatus === 'starting' || fetchedStatus === 'running')
          && Number.isFinite(updatedAtMs)
          && Date.now() - updatedAtMs > STALE_RUNNING_MS
        );

        if (isStaleRunning) {
          setStatus('error');
          setLastSync(fetchedUpdatedAt);
          setExecutionId(summaryLog.executionId || null);
          setProgress(summaryLog.progress || '0 of 0');
          setError('Previous sync status is stale. Please check logs and start a new sync.');
          return;
        }

        // Preserve distinct statuses: 'starting' and 'running'
        if (fetchedStatus === 'starting' || fetchedStatus === 'running') {
          setStatus(fetchedStatus);
        } else {
          setStatus(fetchedStatus || 'idle');
        }
        setLastSync(fetchedUpdatedAt);
        setExecutionId(summaryLog.executionId || null);
        setProgress(summaryLog.progress || '0 of 0');
        setError(fetchedStatus === 'error' ? summaryLog.message : null);
      } else {
        setStatus('idle');
        setLastSync(null);
        setExecutionId(null);
        setProgress('0 of 0');
        setError(null);
      }
    } catch (err) {
      if (!err.message.includes('Session expired')) {
        setError(err.message);
      }
    }
  }, [serviceName]);

  // Check summary logs on mount and when service changes; reset immediately to avoid bleed
  useEffect(() => {
    // Immediate reset to avoid showing previous tab state
    setStatus('idle');
    setLastSync(null);
    setExecutionId(null);
    setProgress('0 of 0');
    setError(null);

    let cancelled = false;
    (async () => {
      try {
        const resp = await flowApi.getConfig(serviceName);
        if (!cancelled) setEnabled(resp?.success ? (resp.data?.enabled !== false) : true);
      } catch (_) { if (!cancelled) setEnabled(true); }
      // Always fetch summary logs on mount
      try {
        if (!cancelled) {
          await checkSummaryLogs();
        }
      } catch (_) {
        if (!cancelled) await checkSummaryLogs();
      }
    })();
    return () => { cancelled = true; };
  }, [serviceName, checkSummaryLogs]);

  // Poll for status updates continuously every 5 seconds (always poll regardless of enabled state)
  useEffect(() => {
    const interval = setInterval(checkSummaryLogs, 5000);
    return () => clearInterval(interval);
  }, [checkSummaryLogs]);

  // React to external enabled/disabled toggles from UI
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.service === serviceName) {
        setEnabled(e.detail.enabled !== false);
      }
    };
    window.addEventListener('flow-enabled-changed', handler);
    return () => window.removeEventListener('flow-enabled-changed', handler);
  }, [serviceName]);

  const startSync = useCallback(async (payload = {}) => {
    try {
      setIsLoading(true);
      setError(null);
      // Immediately show 'starting' state to UI and disable buttons
      setStatus('starting');
      setProgress('0 of 0');
      const now = Date.now();
      setStartAtMs(now);
      setLastSync(new Date(now).toISOString());
      
      const data = await syncApi.startSync(serviceName, payload);
      if (data?.success === false) {
        setStatus('error');
        setError(data.error || 'Failed to start sync');
      } else {
        setExecutionId(data.executionId);
      }
      // Proactively refresh token TTL on user activity
      try { await refresh(); } catch (_) {}
      return data;
    } catch (err) {
      // Don't set error state for session expired errors (they redirect to login)
      if (!err.message.includes('Session expired')) {
        setError(err.message);
        setStatus('error');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [serviceName]);

  const stopSync = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      await syncApi.stopSync(serviceName);
      setStatus('stopped');
      setExecutionId(null);
      
    } catch (err) {
      // Don't set error state for session expired errors (they redirect to login)
      if (!err.message.includes('Session expired')) {
        setError(err.message);
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [serviceName]);

  const refreshStatus = useCallback(() => {
    checkSummaryLogs();
  }, [checkSummaryLogs]);

  return {
    status,
    isLoading,
    error,
    lastSync,
    executionId,
    progress,
    startSync,
    stopSync,
    refreshStatus,
  };
}
